import React, { useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Send as SendIcon } from '@mui/icons-material'
import integrationService from '../../services/integrationService'
import { getErrorMessage } from '../../utils/errorHandlers'

interface EmailTemplateOption {
  id: string
  name: string
  variables: string[]
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const EmailTestMessageTab: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplateOption[]>([])
  const [loading, setLoading] = useState(true)
  const [templateId, setTemplateId] = useState('')
  const [email, setEmail] = useState('')
  const [sampleDataText, setSampleDataText] = useState('{}')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    integrationService
      .getTemplates({ channel: 'email' })
      .then((data) => setTemplates(data as unknown as EmailTemplateOption[]))
      .finally(() => setLoading(false))
  }, [])

  const selectedTemplate = templates.find((t) => t.id === templateId)
  const emailValid = EMAIL_PATTERN.test(email.trim())

  const handleSend = async () => {
    if (!templateId || !email.trim() || !emailValid) return
    setSending(true)
    setResult(null)
    try {
      let sampleData: Record<string, any> = {}
      try {
        sampleData = JSON.parse(sampleDataText || '{}')
      } catch {
        setResult({ success: false, message: 'Sample data must be valid JSON' })
        setSending(false)
        return
      }
      const response = await integrationService.testTemplate(templateId, email.trim(), sampleData)
      setResult({
        success: (response as any).success !== false,
        message: (response as any).message || 'Test email sent',
      })
    } catch (err: any) {
      setResult({
        success: false,
        message: getErrorMessage(err, 'Failed to send test email'),
      })
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box maxWidth={600}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Send a Test Email
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Pick a template, provide a recipient email address, and optionally sample data for its variables — this
          sends a real email via the configured provider(s).
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            select
            label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            fullWidth
          >
            {templates.length === 0 && <MenuItem disabled value="">No email templates found</MenuItem>}
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Recipient Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="test@example.com"
            error={email.trim().length > 0 && !emailValid}
            helperText={email.trim().length > 0 && !emailValid ? 'Invalid email format' : undefined}
            fullWidth
          />

          <TextField
            label="Sample Data (JSON)"
            value={sampleDataText}
            onChange={(e) => setSampleDataText(e.target.value)}
            multiline
            minRows={3}
            helperText={
              selectedTemplate?.variables?.length
                ? `Variables used by this template: ${selectedTemplate.variables.join(', ')}`
                : 'e.g. {"customer_name": "Test User", "order_number": "SO-00001"}'
            }
            fullWidth
          />

          {result && (
            <Alert severity={result.success ? 'success' : 'error'}>{result.message}</Alert>
          )}

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={sending || !templateId || !email.trim() || !emailValid}
            >
              {sending ? 'Sending...' : 'Send Test Email'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default EmailTestMessageTab
